import { useState, useEffect } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { SuperAdminSystemPermissions } from '@wajeez-web/core/types';
import {
  useContentSelector,
  useContentDispatch,
  FlowSelectionsActions,
  FlowsActions,
} from '@wajeez-web/stores/content';
import { HandleTableData } from '@wajeez-web/components/data-table';
import _ from 'lodash';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';

import { ConfirmationModal } from '../components/ConfirmationModal';
import { FlowSelectionModal } from '../components/FlowSelectionModal';
import { VscOpenPreview } from 'react-icons/vsc';
import { PreviewFlowSelectionModal } from '../components/PreviewFlowSelectionModal';

const FlowSelectionsPage = () => {
  const contentDispatch = useContentDispatch();

  const [isFlowSelectionModalOpen, setIsFlowSelectionModalOpen] =
    useState(false);
  const [selectedFlowSelection, setSelectedFlowSelection] = useState<{
    id: number;
    published?: boolean;
  }>();
  const [isSaveFlowSelectionSuccess, setIsSaveFlowSelectionSuccess] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalMessage, setConfirmationModalMessage] =
    useState<string>('');
  const [isPreviewFlowSelectionModalOpen, setIsPreviewFlowSelectionModalOpen] =
    useState(false);

  const dataTableData = useContentSelector(
    ({ flowSelections }) => flowSelections.flowSelectionsDatatable
  );
  const changePublishStateStatus = useContentSelector(
    ({ flowSelections }) => flowSelections.changePublishState.status
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData: getDatatableData,
    isTableReferencesLoading,
  } = HandleTableData(
    'flowSelections',
    FlowSelectionsActions.getFlowSelectionsDatatableAsync,
    undefined,
    undefined,
    [{ property: 1, orderByDirection: 1 }]
  );

  useEffect(() => {
    if (isSaveFlowSelectionSuccess) {
      getDatatableData();

      setIsSaveFlowSelectionSuccess(false);
    }
  }, [isSaveFlowSelectionSuccess]);

  return (
    <>
      <PageLayoutsContainer
        title={`Flow Selections`}
        endSideComponent={[
          {
            element: (
              <div className="flex justify-center items-center space-s-2">
                <Button
                  title={'Preview Flow Selection'}
                  Icon={VscOpenPreview}
                  onClick={() => {
                    setIsPreviewFlowSelectionModalOpen(true);
                  }}
                  bg="primary"
                  color="white"
                />
                <Button
                  title={'Add Flow Selection'}
                  Icon={PlusIcon}
                  onClick={() => {
                    setIsFlowSelectionModalOpen(true);
                  }}
                  bg="primary"
                  color="white"
                />
              </div>
            ),
            permission: [SuperAdminSystemPermissions.flowSelections.add],
          },
        ]}
        breadCrumbsLinks={[{ title: '' }]}
      >
        <DataTable
          dataTableData={dataTableData.data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          renderRowActions={(row) => {
            return (
              <div className="w-full flex justify-center items-center">
                <Dropdown
                  id={`action-${row[1]}`}
                  items={[
                    {
                      label: 'Edit',
                      onClick: () => {
                        setSelectedFlowSelection({
                          id: Number(row[1]) as number,
                        });

                        setIsFlowSelectionModalOpen(true);
                      },
                    },
                    {
                      label: row[2] === 'True' ? 'Unpublish' : 'Publish',
                      onClick: () => {
                        setSelectedFlowSelection({
                          id: Number(row[1]) as number,
                          published: row[2] === 'True' ? true : false,
                        });

                        setIsConfirmationModalOpen(true);

                        if (row[2] === 'True') {
                          setConfirmationModalMessage(
                            'Are you sure that you want to unpublish this flow selection?'
                          );
                        } else {
                          setConfirmationModalMessage(
                            'Are you sure that you want to publish this flow selection?'
                          );
                        }
                      },
                    },
                  ]}
                  label={
                    <div style={{ color: '#343a40' }}>
                      <DotsHorizontalIcon
                        style={{ width: '28px', height: '24px' }}
                      />
                    </div>
                  }
                  buttonStyle={{
                    width: '34px',
                    minWidth: '34px',
                    paddingRight: '2px',
                    paddingLeft: '2px',
                    color: 'black',
                  }}
                />
              </div>
            );
          }}
          isLoading={dataTableData.status === 'loading'}
        />
      </PageLayoutsContainer>
      {isFlowSelectionModalOpen && (
        <FlowSelectionModal
          isOpen={isFlowSelectionModalOpen}
          onClose={() => {
            setIsFlowSelectionModalOpen(false);

            setSelectedFlowSelection(undefined);
          }}
          onSubmit={() => {
            setIsFlowSelectionModalOpen(false);

            setSelectedFlowSelection(undefined);

            setIsSaveFlowSelectionSuccess(true);
          }}
          flowSelectionId={selectedFlowSelection?.id}
        />
      )}
      {isPreviewFlowSelectionModalOpen && (
        <PreviewFlowSelectionModal
          isOpen={isPreviewFlowSelectionModalOpen}
          onClose={() => {
            setIsPreviewFlowSelectionModalOpen(false);

            contentDispatch(FlowsActions.clearSelectionFlow());
          }}
        />
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => {
            setIsConfirmationModalOpen(false);

            setSelectedFlowSelection(undefined);
          }}
          message={confirmationModalMessage}
          onSubmit={async () => {
            const res = await contentDispatch(
              FlowSelectionsActions.changePublishStateAsync({
                flowSelectionId: selectedFlowSelection?.id,
                isPublished: !selectedFlowSelection?.published,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsConfirmationModalOpen(false);

              setSelectedFlowSelection(undefined);

              setIsSaveFlowSelectionSuccess(true);
            }
          }}
          isLoading={changePublishStateStatus === 'loading'}
        />
      )}
    </>
  );
};

export default FlowSelectionsPage;
